import { connectAssetsList } from '@exodus/assets'
import { pick } from '@exodus/basic-utils'
import bip44Constants from '@exodus/bip44-constants/by-ticker.js'
import {
  createEthereumLikeAccountState,
  createGetKeyIdentifier as defaultCreateGetKeyIdentifier,
  createUnsignedTxFactory,
  DEFAULT_FEE_MONITOR_INTERVAL,
  encodePrivate,
  encodePublicFactory,
  getActivityTxs,
  hasChecksum,
  signHardwareFactory,
  signMessage,
  signMessageWithSigner,
  signUnsignedTx,
  signUnsignedTxWithSigner,
  validateFactory,
} from '@exodus/ethereum-lib'
import lodash from 'lodash'
import assert from 'minimalistic-assert'
import ms from 'ms'

import { addressHasHistoryFactory } from './address-has-history.js'
import { createTokenFactory } from './create-token-factory.js'
import { createCustomFeesApi } from './custom-fees.js'
import { createEvmServer } from './exodus-eth-server/index.js'
import { createFeeData } from './fee-data-factory.js'
import { createGetBalanceForAddress } from './get-balance-for-address.js'
import { getBalancesFactory } from './get-balances.js'
import { getEffectiveGasPrice, getFeeFactory } from './get-fee.js'
import getFeeAsyncFactory from './get-fee-async.js'
import { createEthereumHooks } from './hooks/index.js'
import { estimateL1DataFeeFactory, getL1GetFeeFactory } from './optimism-gas/index.js'
import { serverBasedFeeMonitorFactoryFactory } from './server-based-fee-monitor.js'
import { createStakingApi } from './staking-api.js'
import { ClarityMonitor } from './tx-log/clarity-monitor.js'
import { EthereumMonitor } from './tx-log/ethereum-monitor.js'
import { EthereumNoHistoryMonitor } from './tx-log/ethereum-no-history-monitor.js'
import { txSendFactory } from './tx-send/index.js'
import { createWeb3API } from './web3/index.js'

const { mapValues } = lodash

export const createAssetFactory = ({
  AccountState: CustomAccountState,
  assetsList,
  confirmationsNumber,
  customBip44,
  customCreateGetKeyIdentifier,
  customTokens: defaultCustomTokens = true,
  erc20FuelBuffer,
  feeData: providedFeeData,
  feeDataConfig,
  feeMonitorInterval,
  fuelThreshold,
  isMaxFeeAsset = false,
  isTestnet = false,
  l1GasOracleAddress, // l1 extra fee for base and optostakingConfiguration = {},
  monitorInterval,
  monitorType = 'magnifier',
  nfts: defaultNfts = false,
  serverUrl,
  stakingConfiguration = {},
  useEip1191ChainIdChecksum = false,
  forceGasLimitEstimation = false,
  supportsCustomFees: defaultSupportsCustomFees = false,
}) => {
  assert(assetsList, 'assetsList is required')
  assert(providedFeeData || feeDataConfig, 'feeData or feeDataConfig is required')
  assert(serverUrl, 'serverUrl is required')
  assert(confirmationsNumber, 'confirmationsNumber is required')

  const base = assetsList.find((asset) => asset.name === asset.baseAssetName)

  const feeData = providedFeeData || createFeeData({ currency: base.currency, feeDataConfig })
  assert(base, 'base is required')

  const chainId = base.chainId
  assert(chainId, 'chainId is required')

  const createGetKeyIdentifier = customCreateGetKeyIdentifier || defaultCreateGetKeyIdentifier

  const defaultConfig = {
    allowMetaMaskCompat: false,
    supportsStaking: false,
    supportsCustomFees: defaultSupportsCustomFees,
    nfts: defaultNfts,
    customTokens: defaultCustomTokens,
  }
  return (
    {
      assetClientInterface,
      config = Object.create(null),
      overrideCallback = ({ asset }) => asset,
    } = Object.create(null)
  ) => {
    const assets = connectAssetsList(assetsList)

    const { allowMetaMaskCompat, supportsStaking, nfts, customTokens, supportsCustomFees } = {
      ...defaultConfig,
      ...config,
    }

    const asset = assets[base.name]

    const server = createEvmServer({ assetName: asset.name, serverUrl, monitorType })

    const gasLimit = 21e3 // 21 KGas, enough only for sending ether to normal address

    const contractGasLimit = 1e6 // used when estimateGas fail

    const address = {
      validate: validateFactory({ chainId, useEip1191ChainIdChecksum }),
      hasChecksum,
      isContract: server.isContract,
    }

    const bip44 = customBip44 || bip44Constants['ETH']

    const keys = {
      encodePrivate,
      encodePublic: encodePublicFactory({ chainId, useEip1191ChainIdChecksum }),
    }

    const getBalances = getBalancesFactory({
      monitorType,
      config,
    })

    const { createToken, getTokens } = createTokenFactory(
      { address, bip44, keys, getBalances },
      assets
    )

    const addressHasHistory = addressHasHistoryFactory({ server })

    const isWebSocket = monitorType !== 'no-history'

    const resolvedFeeMonitorInterval =
      config.feeMonitorInterval ||
      feeMonitorInterval ||
      (isWebSocket ? null : DEFAULT_FEE_MONITOR_INTERVAL) // null means do not start the timer

    const createFeeMonitor = serverBasedFeeMonitorFactoryFactory({
      assetName: base.name,
      interval: resolvedFeeMonitorInterval,
      eip1559Enabled: feeData.eip1559Enabled, // this is not updated via remote config. Should it be?
      server,
    })

    const features = {
      accountState: true,
      customTokens,
      feeMonitor: true,
      feesApi: true,
      isMaxFeeAsset,
      isTestnet,
      nfts,
      noHistory: monitorType === 'no-history',
      signWithSigner: true,
      signMessageWithSigner: true,
      supportsCustomFees,
      ...(supportsStaking && { staking: {} }),
    }

    const stakingAssetNames = Object.keys(stakingConfiguration)

    const extraData =
      stakingAssetNames.length > 0
        ? {
            staking: mapValues(
              stakingConfiguration,
              (assetStakinConfiguration) => assetStakinConfiguration.accountStateExtraData
            ),
          }
        : undefined

    const accountStateClass =
      CustomAccountState || createEthereumLikeAccountState({ asset: base, assets, extraData })

    const createHistoryMonitor = (args) => {
      let monitor
      switch (monitorType) {
        case 'clarity':
        case 'clarity-v2':
          monitor = new ClarityMonitor({
            assetClientInterface,
            interval: ms(monitorInterval || '5m'),
            server,
            ...args,
          })
          break
        case 'no-history':
          monitor = new EthereumNoHistoryMonitor({
            assetClientInterface,
            interval: ms(monitorInterval || '15s'),
            server,
            ...args,
          })
          break
        case 'magnifier':
          monitor = new EthereumMonitor({
            assetClientInterface,
            interval: ms(monitorInterval || '15s'),
            server,
            ...args,
          })
          break
        default:
          throw new Error(`Monitor type ${monitorType} of evm asset ${asset.name} is unknown`)
      }

      if (stakingAssetNames.length > 0) {
        const afterTickHook = createEthereumHooks({
          assetClientInterface,
          assetName: asset.name,
          stakingAssetNames,
          server,
        })['after-tick']
        monitor.addHook('after-tick', afterTickHook)
      }

      return monitor
    }

    const defaultAddressPath = 'm/0/0'

    const createUnsignedTx = createUnsignedTxFactory({ chainId })

    const sendTx = txSendFactory({
      assetClientInterface,
      createUnsignedTx,
    })

    const estimateL1DataFee = l1GasOracleAddress
      ? estimateL1DataFeeFactory({ l1GasOracleAddress, server })
      : undefined

    const originalGetFee = getFeeFactory({ gasLimit })

    const getFee = l1GasOracleAddress
      ? getL1GetFeeFactory({ asset, originalGetFee })
      : originalGetFee

    const api = {
      getActivityTxs,
      addressHasHistory,
      broadcastTx: (...args) => server.sendRawTransaction(...args),
      createAccountState: () => accountStateClass,
      createFeeMonitor,
      createHistoryMonitor,
      createToken,
      createUnsignedTx,
      customFees: createCustomFeesApi({ baseAsset: asset }),
      defaultAddressPath,
      features,
      getBalances,
      getBalanceForAddress: createGetBalanceForAddress({ asset, server }),
      getConfirmationsNumber: () => confirmationsNumber,
      getDefaultAddressPath: () => defaultAddressPath,
      getFeeAsync: getFeeAsyncFactory({ assetClientInterface, gasLimit, createUnsignedTx }),
      getFee,
      getFeeData: () => feeData,
      getKeyIdentifier: createGetKeyIdentifier({
        bip44,
        allowMetaMaskCompat,
        assetName: asset.name,
      }),
      getSupportedPurposes: () => [44],
      getTokens,
      hasFeature: (feature) => !!features[feature], // @deprecated use api.features instead
      privateKeyEncodingDefinition: { encoding: 'hex' },
      sendTx,
      signTx: ({ unsignedTx, privateKey, signer }) =>
        signer
          ? signUnsignedTxWithSigner(unsignedTx, signer)
          : signUnsignedTx(unsignedTx, privateKey),
      signHardware: signHardwareFactory({ baseAssetName: asset.name }),
      signMessage: ({ message, privateKey, signer }) =>
        signer ? signMessageWithSigner({ message, signer }) : signMessage({ privateKey, message }),
      ...(supportsStaking && { staking: createStakingApi({ network: asset.name }) }),
      validateAssetId: address.validate,
      web3: createWeb3API({ asset }),
    }

    const fullAsset = {
      ...asset,
      ...pick(config, ['accountReserve']),
      gasLimit,
      contractGasLimit,
      bip44,
      keys,
      address,
      api,
      chainId,
      monitorType,
      estimateL1DataFee,
      forceGasLimitEstimation,
      server,
      ...(erc20FuelBuffer && { erc20FuelBuffer }),
      ...(fuelThreshold && { fuelThreshold: asset.currency.defaultUnit(fuelThreshold) }),
      getEffectiveGasPrice,
    }
    return overrideCallback({
      asset: fullAsset,
      stakingConfiguration,
      assetClientInterface,
      monitorInterval,
    })
  }
}
